/**
 * Helpers that interpret OSM tags on park-and-ride facility payloads for itinerary UI.
 */

import { type ItineraryLegDTO, type ItineraryLegStepDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type ParkAndRideDetailsDTO } from "../types/dtos/api/itinerary/park-and-ride-details.dto";
import { getTransportModeGroup, TransportModeGroup } from "./TransportModeGroupsTools";

/** OSM tag map attached to a park-and-ride facility. */
export type ParkAndRideOsmTags = Readonly<Record<string, string>>;

/** Ternary OSM yes/no/unknown state used by amenity capsules. */
export type OsmTriState = "yes" | "no" | "unknown";

/** Capacity capsule state derived from OSM capacity tags. */
export type OsmCapacityState =
  | {
      "kind":  "available";
      "count": number | null;
  } |
  { "kind": "unavailable" } |
  { "kind": "unknown" };

/** Amenity capsule kinds rendered under a mycar→P+R leg. */
export type ParkAndRideAmenityKind =
  | "fee" |
  "wheelchair" |
  "car" |
  "charging" |
  "bike" |
  "motorcycle";

/** Resolved amenity capsule ready for presentation. */
export interface ParkAndRideAmenityCapsule {

    /** Capsule semantic kind. */
    "kind": ParkAndRideAmenityKind;

    /** When `true`, the amenity is unavailable (strikethrough + red). */
    "unavailable": boolean;

    /** Optional numeric capacity shown next to the icon. */
    "count": number | null;
}

/** Display model for a park-and-ride destination under a mycar leg. */
export interface ParkAndRideDestinationDisplay {

    /** Destination title without the `vers` prefix. */
    "name": string;

    /** Amenity capsules derived from `osmTags` (absent kinds omitted). */
    "capsules": readonly ParkAndRideAmenityCapsule[];

    /** Formatted address line, or `null` when no address parts exist. */
    "address": string | null;

    /** Compact, localized opening-hours label, or `null` when unavailable. */
    "openingHours": string | null;

    /** Operator label for the website link title, or `null`. */
    "operator": string | null;

    /** Absolute website URL, or `null` when missing/invalid. */
    "website": string | null;
}

/**
 * Whether a leg step is a FlowRide park-and-ride waypoint.
 *
 * @param step Leg endpoint or intermediate stop.
 * @returns `true` when `modes` includes `park_and_ride`.
 */
export const isParkAndRideStep = ( step: ItineraryLegStepDTO | undefined | null ): boolean => {
    if ( step == null ) {
        return false;
    }

    return step.modes?.includes( "park_and_ride" ) === true;
};

/**
 * Whether a mycar/car leg destinations at a park-and-ride facility.
 *
 * @param leg Itinerary leg DTO.
 * @returns `true` for car/mycar legs whose `to` is a P+R step.
 */
export const isMyCarLegToParkAndRide = ( leg: ItineraryLegDTO ): boolean => {
    const modeGroup = getTransportModeGroup( leg.mode );
    if ( modeGroup !== TransportModeGroup.MY_CAR && modeGroup !== TransportModeGroup.CAR ) {
        return false;
    }

    return isParkAndRideStep( leg.to );
};

/**
 * Resolves the display name for a P+R destination step.
 *
 * @param step Destination step expected to be a P+R waypoint.
 * @returns Non-empty name, or `null` when unavailable.
 */
export const resolveParkAndRideDestinationName = ( step: ItineraryLegStepDTO | undefined | null ): string | null => {
    if ( step == null ) {
        return null;
    }

    const stepName = step.name?.trim();
    if ( stepName != null && stepName.length > 0 ) {
        return stepName;
    }

    const detailsName = step.details?.name?.trim();
    if ( detailsName != null && detailsName.length > 0 ) {
        return detailsName;
    }

    return null;
};

/**
 * Normalizes an OSM yes/no-like string into a tri-state.
 *
 * @param raw Raw OSM tag value.
 * @returns `yes`, `no`, or `unknown`.
 */
export const parseOsmTriState = ( raw: string | undefined | null ): OsmTriState => {
    if ( raw == null ) {
        return "unknown";
    }

    const normalized = raw.trim().toLowerCase();
    if ( normalized === "yes" || normalized === "true" || normalized === "1" ) {
        return "yes";
    }

    if ( normalized === "no" || normalized === "false" || normalized === "0" ) {
        return "no";
    }

    return "unknown";
};

/**
 * Parses a non-negative integer capacity from an OSM tag string.
 *
 * @param raw Raw OSM tag value.
 * @returns Parsed integer, or `null` when missing/invalid.
 */
export const parseOsmCapacityCount = ( raw: string | undefined | null ): number | null => {
    if ( raw == null ) {
        return null;
    }

    const trimmed = raw.trim();
    if ( trimmed.length === 0 ) {
        return null;
    }

    const parsed = Number.parseInt( trimmed, 10 );
    if ( !Number.isFinite( parsed ) || parsed < 0 ) {
        return null;
    }

    return parsed;
};

/**
 * Resolves capacity state preferring a dedicated key, with optional fallback key.
 *
 * @param tags OSM tag map.
 * @param primaryKey Preferred capacity key (e.g. `capacity:motorcar`).
 * @param fallbackKey Optional fallback key (e.g. `capacity`).
 * @returns Available / unavailable / unknown state.
 */
export const resolveOsmCapacityState = (
    tags: ParkAndRideOsmTags,
    primaryKey: string,
    fallbackKey?: string,
): OsmCapacityState => {
    const primaryCount = parseOsmCapacityCount( tags[primaryKey] );
    if ( primaryCount != null ) {
        return primaryCount > 0
            ? {
                "kind":  "available",
                "count": primaryCount,
            }
            : { "kind": "unavailable" };
    }

    if ( fallbackKey != null ) {
        const fallbackCount = parseOsmCapacityCount( tags[fallbackKey] );
        if ( fallbackCount != null ) {
            return fallbackCount > 0
                ? {
                    "kind":  "available",
                    "count": fallbackCount,
                }
                : { "kind": "unavailable" };
        }

        /*
         * Both keys present as explicit zero is handled above; when only one is known we already returned.
         * Unavailable only when both keys are explicitly zero — if primary missing and fallback is 0, unavailable.
         */
    }

    return { "kind": "unknown" };
};

/**
 * Resolves car parking capacity, preferring `capacity:motorcar` over `capacity`.
 * Unavailable only when both known values are explicitly `0`.
 *
 * @param tags OSM tag map.
 * @returns Capacity state for the car capsule.
 */
export const resolveCarCapacityState = ( tags: ParkAndRideOsmTags ): OsmCapacityState => {
    const motorcarCount = parseOsmCapacityCount( tags["capacity:motorcar"] );
    const capacityCount = parseOsmCapacityCount( tags.capacity );

    if ( motorcarCount != null && motorcarCount > 0 ) {
        return {
            "kind":  "available",
            "count": motorcarCount,
        };
    }

    if ( capacityCount != null && capacityCount > 0 ) {
        return {
            "kind":  "available",
            "count": capacityCount,
        };
    }

    if ( motorcarCount === 0 && capacityCount === 0 ) {
        return { "kind": "unavailable" };
    }

    return { "kind": "unknown" };
};

/**
 * Builds the address line from OSM `addr:*` tags.
 *
 * @param tags OSM tag map.
 * @returns Formatted address, or `null` when empty.
 */
export const formatParkAndRideAddressFromOsmTags = ( tags: ParkAndRideOsmTags ): string | null => {
    const houseNumber = tags["addr:housenumber"]?.trim() ?? "";
    const street = tags["addr:street"]?.trim() ?? "";
    const postcode = tags["addr:postcode"]?.trim() ?? "";
    const city = tags["addr:city"]?.trim() ?? "";

    const streetPart = [ houseNumber, street ].filter( ( part ) => part.length > 0 ).join( " " );
    const cityPart = [ postcode, city ].filter( ( part ) => part.length > 0 ).join( " " );

    if ( streetPart.length === 0 && cityPart.length === 0 ) {
        return null;
    }

    if ( streetPart.length === 0 ) {
        return cityPart;
    }

    if ( cityPart.length === 0 ) {
        return streetPart;
    }

    return `${ streetPart }, ${ cityPart }`;
};

/** French abbreviations used to localize standard OSM weekday tokens. */
const OSM_WEEKDAY_LABELS: Readonly<Record<string, string>> = {
    "Mo": "Lu",
    "Tu": "Ma",
    "We": "Me",
    "Th": "Je",
    "Fr": "Ve",
    "Sa": "Sa",
    "Su": "Di",
    "PH": "jours fériés",
};

/**
 * Formats an OSM `opening_hours` expression for compact display.
 *
 * @remarks This intentionally performs only lossless presentation changes. It
 * does not infer whether the facility is currently open, because full OSM
 * expressions require a dedicated parser and timezone-aware evaluation.
 * @param raw Raw `opening_hours` expression.
 * @returns Compact French label, or `null` when empty.
 */
export const formatParkAndRideOpeningHours = ( raw: string | undefined | null ): string | null => {
    if ( raw == null ) {
        return null;
    }

    const trimmed = raw.trim();
    if ( trimmed.length === 0 ) {
        return null;
    }

    if ( trimmed.toLowerCase() === "24/7" ) {
        return "24/7";
    }

    let formatted = trimmed;
    formatted = formatted.replace( /\b(Mo|Tu|We|Th|Fr|Sa|Su|PH)\b/g, ( token ) => OSM_WEEKDAY_LABELS[token] ?? token );
    formatted = formatted.replace( /\b(Lu|Ma|Me|Je|Ve|Sa|Di)-(Lu|Ma|Me|Je|Ve|Sa|Di)\b/g, "$1–$2" );
    formatted = formatted.replace( /(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/g, "$1–$2" );
    formatted = formatted.replace( /\b((?:Lu|Ma|Me|Je|Ve|Sa|Di)(?:–(?:Lu|Ma|Me|Je|Ve|Sa|Di))?)\s+(?=\d{1,2}:\d{2})/g, "$1 · " );
    formatted = formatted.replace( /\s*;\s*/g, " ; " );

    return formatted;
};

/**
 * Normalizes a website URL for use as an anchor href.
 *
 * @param raw Raw website string from OSM tags or DTO fields.
 * @returns Absolute http(s) URL, or `null` when unusable.
 */
export const normalizeParkAndRideWebsiteUrl = ( raw: string | undefined | null ): string | null => {
    if ( raw == null ) {
        return null;
    }

    const trimmed = raw.trim();
    if ( trimmed.length === 0 ) {
        return null;
    }

    if ( ( /^https?:\/\//i ).test( trimmed ) ) {
        return trimmed;
    }

    return `https://${ trimmed }`;
};

/**
 * Builds amenity capsules from a park-and-ride `osmTags` map.
 *
 * @param tags OSM tag map (may be empty).
 * @returns Capsules for known amenities only.
 */
export const buildParkAndRideAmenityCapsules = ( tags: ParkAndRideOsmTags ): readonly ParkAndRideAmenityCapsule[] => {
    const capsules: ParkAndRideAmenityCapsule[] = [];

    const feeState = parseOsmTriState( tags.fee );
    if ( feeState === "yes" ) {
        capsules.push( {
            "kind":        "fee",
            "unavailable": false,
            "count":       null,
        } );
    } else if ( feeState === "no" ) {
        capsules.push( {
            "kind":        "fee",
            "unavailable": true,
            "count":       null,
        } );
    }

    const wheelchairState = parseOsmTriState( tags.wheelchair );
    if ( wheelchairState === "yes" ) {
        capsules.push( {
            "kind":        "wheelchair",
            "unavailable": false,
            "count":       parseOsmCapacityCount( tags["capacity:disabled"] ),
        } );
    } else if ( wheelchairState === "no" ) {
        capsules.push( {
            "kind":        "wheelchair",
            "unavailable": true,
            "count":       null,
        } );
    }

    const carState = resolveCarCapacityState( tags );
    if ( carState.kind === "available" ) {
        capsules.push( {
            "kind":        "car",
            "unavailable": false,
            "count":       carState.count,
        } );
    } else if ( carState.kind === "unavailable" ) {
        capsules.push( {
            "kind":        "car",
            "unavailable": true,
            "count":       null,
        } );
    }

    const chargingState = resolveOsmCapacityState( tags, "capacity:charging" );
    if ( chargingState.kind === "available" ) {
        capsules.push( {
            "kind":        "charging",
            "unavailable": false,
            "count":       chargingState.count,
        } );
    } else if ( chargingState.kind === "unavailable" ) {
        capsules.push( {
            "kind":        "charging",
            "unavailable": true,
            "count":       null,
        } );
    }

    const motorcycleState = resolveOsmCapacityState( tags, "capacity:motorcycle" );
    if ( motorcycleState.kind === "available" ) {
        capsules.push( {
            "kind":        "motorcycle",
            "unavailable": false,
            "count":       motorcycleState.count,
        } );
    } else if ( motorcycleState.kind === "unavailable" ) {
        capsules.push( {
            "kind":        "motorcycle",
            "unavailable": true,
            "count":       null,
        } );
    }

    const bikeState = resolveOsmCapacityState( tags, "capacity:bike" );
    if ( bikeState.kind === "available" ) {
        capsules.push( {
            "kind":        "bike",
            "unavailable": false,
            "count":       bikeState.count,
        } );
    } else if ( bikeState.kind === "unavailable" ) {
        capsules.push( {
            "kind":        "bike",
            "unavailable": true,
            "count":       null,
        } );
    }

    return capsules;
};

/**
 * Builds the full P+R destination display model for a leg step.
 *
 * @param step Destination step with optional `details`.
 * @param detailsOverride Optional details when already resolved.
 * @returns Display model, or `null` when the step is not a usable P+R destination.
 */
export const buildParkAndRideDestinationDisplay = (
    step: ItineraryLegStepDTO | undefined | null,
    detailsOverride?: ParkAndRideDetailsDTO | null,
): ParkAndRideDestinationDisplay | null => {
    if ( !isParkAndRideStep( step ) ) {
        return null;
    }

    const details = detailsOverride ?? step?.details ?? null;
    const name = resolveParkAndRideDestinationName( step );
    if ( name == null ) {
        return null;
    }

    const tags: ParkAndRideOsmTags = details?.osmTags ?? {};
    const operatorFromTags = tags.operator?.trim() ?? "";
    const operatorFromDetails = details?.operator?.trim() ?? "";
    const operator = operatorFromTags.length > 0
        ? operatorFromTags
        : operatorFromDetails.length > 0
            ? operatorFromDetails
            : null;

    const websiteFromTags = normalizeParkAndRideWebsiteUrl( tags.website );
    const websiteFromDetails = normalizeParkAndRideWebsiteUrl( details?.website ?? null );
    const website = websiteFromTags ?? websiteFromDetails;

    return {
        name,
        "capsules":     buildParkAndRideAmenityCapsules( tags ),
        "address":      formatParkAndRideAddressFromOsmTags( tags ),
        "openingHours": formatParkAndRideOpeningHours( details?.openingHours ?? tags.opening_hours ),
        operator,
        website,
    };
};
